import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import useNow from '@hooks/use-now.ts';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  DeleteBlackIcon,
  EditBlackIcon
} from '@components/icons/svg-icon.tsx';
import {
  ScheduleCardProps,
  ScheduleStatusEnum,
  ScheduleTimeStatusEnum
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  SCHEDULE_DAY_APPEARANCE,
  SCHEDULE_STATUS_TONES,
  SCHEDULE_TIME_STATUS_CONFIG,
  getDayAppearance
} from '@modules/digital-signage/utils/schedule-appearance.utils.ts';
import { WEEK_DAYS } from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  formatDateRange,
  getDayTimeline,
  getRangeStatus,
  getScheduleStatus,
  getTimelineHint
} from '@modules/digital-signage/utils/schedule-status.utils.ts';
import { formatRange } from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  getSavedDayRanges,
  getScheduleSlots,
  isSavedRange
} from '@modules/digital-signage/utils/schedule-slots.utils.ts';
import { getScheduleDates } from '@modules/digital-signage/utils/schedule-date.utils.ts';
import ScheduleStatusBadge from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-status-badge/schedule-status-badge.tsx';
import StatusLozenge from '@modules/digital-signage/components/group-settings/components/status-lozenge/status-lozenge.tsx';
import { StatusLozengeToneEnum } from '@modules/digital-signage/model/group-settings.model.ts';
import { STATUS_LOZENGE_TONES } from '@modules/digital-signage/utils/digital-signage.utils.ts';

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  schedule,
  color,
  savedSlotKeys,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  const now = dayjs(useNow(60000));
  const status = getScheduleStatus(schedule, now, savedSlotKeys);
  const isEnded = status === ScheduleStatusEnum.Ended;

  const period = useMemo(() => getScheduleDates(schedule), [schedule]);
  const scheduleSlots = useMemo(() => getScheduleSlots(schedule), [schedule]);
  const dayTimelines = new Map(
    WEEK_DAYS.flatMap((day) => {
      const ranges = scheduleSlots
        .filter((slot) => slot.days.includes(day))
        .map((slot) => slot.range);
      return ranges.length
        ? [
            [
              day,
              getDayTimeline(
                day,
                getSavedDayRanges(day, ranges, savedSlotKeys),
                period,
                now
              )
            ] as const
          ]
        : [];
    })
  );
  const slots = scheduleSlots.map((slot) => ({
    time: formatRange(slot.range, t),
    status: getRangeStatus(
      slot.days,
      isSavedRange(slot.days, slot.range, savedSlotKeys) ? slot.range : null,
      period,
      now
    )
  }));
  const primarySlot =
    slots.find((slot) => slot.status === ScheduleTimeStatusEnum.Live) ??
    slots[0];
  const dateRange = formatDateRange(schedule, t);

  return (
    <div className="relative flex flex-col gap-2.5 py-3 pr-3 pl-4.5 rounded-[5px] border border-(--blue-11) bg-(--white) transition-colors hover:border-(--primary-400)">
      <span
        className="absolute left-0 inset-y-0 w-1 rounded-l-[9px]"
        style={{ backgroundColor: isEnded ? 'var(--gray-3)' : color }}
      />

      <div className="flex items-center gap-2 min-w-0">
        <Tooltip title={schedule.name} placement="top">
          <Typography
            variant="body1"
            className="font-semibold! text-(--dark-1)! truncate min-w-0"
          >
            {schedule.name}
          </Typography>
        </Tooltip>
        <StatusLozenge
          tone={SCHEDULE_STATUS_TONES[status]}
          label={t(`digital_signage_schedule_status_${status}`)}
        />

        <div className="flex gap-1.5 ml-auto shrink-0">
          <IconButton
            aria-label={t('edit')}
            onClick={() => onEdit(schedule)}
            className="w-7.5! h-7.5! bg-(--gray-11)! hover:bg-(--primary-100)!"
          >
            <EditBlackIcon className="w-3.5! h-3.5!" />
          </IconButton>
          <IconButton
            aria-label={t('delete')}
            onClick={() => onDelete(schedule)}
            color="error"
            className="shrink-0 w-7.5! h-7.5! bg-(--gray-11)! hover:bg-(--gray-10)!"
          >
            <DeleteBlackIcon className="w-4! h-4!" />
          </IconButton>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEK_DAYS.map((day) => {
          const timeline = dayTimelines.get(day);
          return (
            <Tooltip
              key={day}
              placement="top"
              title={timeline ? getTimelineHint(timeline, t) : ''}
            >
              <Typography
                variant="caption"
                component="span"
                className={`h-6 rounded-md flex! items-center justify-center text-[11px]! ${
                  SCHEDULE_DAY_APPEARANCE[
                    getDayAppearance({
                      selected: !!timeline,
                      status: timeline?.status,
                      disabled: true
                    })
                  ].cardDay
                }`}
              >
                {t(`digital_signage_schedule_day_${day}`)}
              </Typography>
            </Tooltip>
          );
        })}
      </div>

      <div className="flex items-center gap-3 min-w-0 text-(--blue-4)">
        <div className="flex items-center gap-1.5 shrink-0">
          <ClockIcon width={14} color="currentColor" />
          {primarySlot && (
            <Tooltip
              placement="top"
              title={
                SCHEDULE_TIME_STATUS_CONFIG[primarySlot.status].tone
                  ? t(SCHEDULE_TIME_STATUS_CONFIG[primarySlot.status].label)
                  : ''
              }
            >
              <Typography
                variant="caption"
                component="span"
                className={`px-2 py-px rounded-md border text-xs! whitespace-nowrap ${
                  SCHEDULE_TIME_STATUS_CONFIG[primarySlot.status].cardTime
                }`}
              >
                {primarySlot.time}
              </Typography>
            </Tooltip>
          )}
          {slots.length > 1 && (
            <Tooltip
              placement="top"
              title={
                <div className="flex flex-col gap-1">
                  <Typography
                    variant="caption"
                    component="span"
                    className="font-semibold! text-inherit! text-[length:inherit]!"
                  >
                    {t('digital_signage_schedule_all_time_slots')}
                  </Typography>
                  {slots.map((slot, index) => (
                    <span key={index} className="flex items-center gap-1.5">
                      {slot.time}
                      <ScheduleStatusBadge status={slot.status} />
                    </span>
                  ))}
                </div>
              }
            >
              <Typography
                variant="caption"
                component="span"
                className={`px-2 rounded-full text-[11px]! font-semibold! leading-4.5! cursor-default ${
                  STATUS_LOZENGE_TONES[StatusLozengeToneEnum.BrandSoft]
                }`}
              >
                +{slots.length - 1}
              </Typography>
            </Tooltip>
          )}
        </div>

        <div className="flex items-center gap-1.5 min-w-0">
          <CalendarIcon
            fill="currentColor"
            className="w-3.5! h-3.5! shrink-0"
          />
          <Tooltip title={dateRange} placement="top">
            <Typography
              variant="caption"
              component="span"
              className="truncate text-[13px]! text-inherit! font-[inherit]!"
            >
              {dateRange}
            </Typography>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCard;
